/**
 * GuestVisit — TV-P3-06
 *
 * Lands interpreters / observers directly into a LiveKit room
 * without a SMART launch. The provider mints a guest token via
 * generateLivekitToken (role=guest) and copies a link of the form:
 *
 *   /guest-visit#payload=<base64url(JSON({token, url, room, name}))>
 *
 * The payload is in the URL hash, NOT the query string, so it
 * never reaches the server access log. The token is short-lived
 * (1hr server TTL) and only grants `roomJoin + canPublish +
 * canSubscribe` for the named room — losing the link is bounded.
 *
 * Wired into App.jsx OUTSIDE the ProtectedRoute tree so guests
 * don't get bounced to KC login.
 */

import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { Mic, MicOff, Camera, CameraOff, LogOut, AlertTriangle } from 'lucide-react';

function decodePayload(hash) {
  if (!hash) return null;
  // Strip leading '#' and pull the payload param.
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const raw = params.get('payload');
  if (!raw) return null;
  try {
    const json = atob(raw.replace(/-/g, '+').replace(/_/g, '/'));
    const obj = JSON.parse(json);
    if (!obj?.token || !obj?.url) return null;
    return obj;
  } catch {
    return null;
  }
}

function GuestTiles() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  return (
    <GridLayout tracks={tracks} className="h-full">
      <ParticipantTile />
    </GridLayout>
  );
}

function GuestControls({ onLeave }) {
  const { localParticipant } = useLocalParticipant();
  const micEnabled = localParticipant?.isMicrophoneEnabled ?? false;
  const camEnabled = localParticipant?.isCameraEnabled ?? false;

  return (
    <div className="bg-slate-900 border-t border-slate-800 px-6 py-4">
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => localParticipant?.setMicrophoneEnabled(!micEnabled)}
          className="flex flex-col items-center gap-1 p-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white"
        >
          {micEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          <span className="text-xs">{micEnabled ? 'Mic On' : 'Mic Off'}</span>
        </button>
        <button
          onClick={() => localParticipant?.setCameraEnabled(!camEnabled)}
          className="flex flex-col items-center gap-1 p-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white"
        >
          {camEnabled ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}
          <span className="text-xs">{camEnabled ? 'Camera On' : 'Camera Off'}</span>
        </button>
        <button
          onClick={onLeave}
          className="flex flex-col items-center gap-1 p-3 rounded-lg bg-red-600 hover:bg-red-700 text-white"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-xs">Leave</span>
        </button>
      </div>
    </div>
  );
}

export default function GuestVisit() {
  const { hash } = useLocation();
  const payload = useMemo(() => decodePayload(hash), [hash]);
  const [left, setLeft] = useState(false);

  if (!payload) {
    return (
      <div className="h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-300">
        <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm font-medium">Invalid or expired invite link</p>
        <p className="text-xs text-slate-500 mt-1 max-w-md text-center">
          Ask the provider to send a fresh invite — guest tokens expire
          one hour after they're issued.
        </p>
      </div>
    );
  }
  if (left) {
    return (
      <div className="h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-300">
        <p className="text-sm font-medium">You've left the visit.</p>
        <p className="text-xs text-slate-500 mt-1">You can close this tab.</p>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={payload.token}
      serverUrl={payload.url}
      connect
      audio
      video
      data-lk-theme="default"
      className="h-screen flex flex-col bg-slate-950"
      onDisconnected={() => setLeft(true)}
    >
      <div className="bg-slate-900 px-6 py-2 border-b border-slate-800">
        <p className="text-xs text-slate-400">
          You're joining as <span className="font-semibold text-slate-200">{payload.name || 'Guest'}</span>
        </p>
      </div>
      <div className="flex-1 p-4 overflow-hidden">
        <GuestTiles />
      </div>
      <RoomAudioRenderer />
      <GuestControls onLeave={() => setLeft(true)} />
    </LiveKitRoom>
  );
}
